/**
 * Global UI Store (V3)
 * Manages the "OS Shell" state - layout, navigation, theme, timeline, and date context
 *
 * @description Zustand store with localStorage persistence for UI preferences
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { ThemePalette } from '~/core/utils/colorUtils';
import { DEFAULT_PINNED_MODULES, REQUIRED_MODULES, VALID_MODULE_IDS, getModuleDefinition, type ModuleSection } from '~/core/config/modules';

// ============================================================================
// TYPES
// ============================================================================

/** Available application modules */
export type AppModule = 'DISPATCHER' | 'CALENDAR' | 'SESSIONS' | 'STUDENT_MANAGE' | 'SCHOOLS' | 'FLOWS' | 'MESSAGES' | 'SCORES' | 'STATS' | 'TEXTBOOKS' | 'NOTEBOOKS' | 'QUESTIONS' | 'INK';

/** Theme preference options */
export type ThemePreference = 'light' | 'dark' | 'system';

/** Design style options - includes built-in themes and custom-{id} pattern */
export type BuiltInDesignStyle = 'soft' | 'brutal' | 'neon' | 'pastel' | 'jeju' | 'mono' | 'forest' | 'deepgreen';
export type DesignStyle = BuiltInDesignStyle | `custom-${string}`;

/** Custom theme palettes storage */
export interface CustomThemePalette {
  light: ThemePalette;
  dark: ThemePalette;
}

/** ActiveDate 변경 출처 - 피드백 루프 방지용 */
export type ActiveDateSource =
  | 'timeline-scroll'    // 타임라인 스크롤로 인한 변경
  | 'datereel-click'     // DateReel 날짜 클릭
  | 'datereel-pan'       // DateReel 드래그/휠
  | 'go-to-today'        // 오늘로 복귀
  | 'nav-button'         // 이전/다음 버튼
  | 'year-view'          // YearView 캘린더 히트맵 클릭
  | 'init';              // 초기화

/** Local dayKey 생성 (UTC 아닌 로컬 기준) */
export function getLocalDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

// ============================================================================
// TIMELINE CONSTANTS (Per "The Sequencer" - Professional Zoom Engine)
// ============================================================================

/**
 * Hour Width Constraints (pixels per hour)
 *
 * Zoom Levels:
 * - 60px:  Overview (see full day at a glance)
 * - 100px: Default (balanced view)
 * - 200px: Detail (15-min visibility)
 * - 400px: High Detail (5-min visibility)
 * - 600px: Maximum (precise editing)
 */
export const HOUR_WIDTH_MIN = 10;
export const HOUR_WIDTH_MAX = 600;
export const HOUR_WIDTH_DEFAULT = 200;

/** 이 값 이하면 세션을 점(dot)으로 렌더링 (20% zoom = 40px) */
export const DOT_MODE_THRESHOLD = 40;

/** 이 값 이하면 TimeRuler를 day-only 모드로 표시 */
export const DAY_MODE_THRESHOLD = 30;

/**
 * Zoom Step - Dynamic based on current zoom level for "elastic" feel
 * At low zoom: larger steps for faster navigation
 * At high zoom: smaller steps for precision
 */
export const HOUR_WIDTH_STEP = 20;

/** Timeline coverage: 24 hours (0-23) */
export const TIMELINE_START_HOUR = 0;
export const TIMELINE_END_HOUR = 24;
export const TIMELINE_HOURS = TIMELINE_END_HOUR - TIMELINE_START_HOUR;

/** Default scroll position: 13:00 (1 PM) */
export const DEFAULT_SCROLL_HOUR = 13;

// ============================================================================
// STATE TYPES
// ============================================================================

/** Global UI state shape */
export interface GlobalState {
  // Layout
  /** Whether the secondary sidebar is open */
  isSidebarOpen: boolean;
  /** Whether the Dock (left nav) is collapsed */
  isDockCollapsed: boolean;
  /** Whether the Finder (sidebar) is collapsed */
  isFinderCollapsed: boolean;

  // Navigation
  /** Currently active application module */
  currentModule: AppModule;

  // Theme
  /** User's theme preference */
  theme: ThemePreference;
  /** Whether dark mode is currently active */
  isDarkMode: boolean;
  /** Design style (soft = modern rounded, brutal = neubrutalism) */
  designStyle: DesignStyle;

  // Custom Theme
  /** Active custom theme ID (null = using built-in theme) */
  activeCustomThemeId: string | null;
  /** Custom theme palettes cache (themeId -> { light, dark }) */
  customThemePalettes: Record<string, CustomThemePalette>;

  // Timeline Mechanics (V3 - Sequencer Core)
  /** Pixels per hour - controls zoom level (60px to 600px) */
  hourWidth: number;
  /** Timeline start hour (0-23) - usually 0 for 24-hour coverage */
  baseStartTime: number;

  // Date Context
  /** Currently active/focused date in the timeline */
  activeDate: Date;
  /** 마지막 activeDate 변경 출처 */
  activeDateSource: ActiveDateSource;
  /** 로컬 기준 dayKey (캐시) */
  activeDateKey: string;
  /** 스크롤 목표 시간 (hour, 0-24). null이면 기본값 12시(정오) 사용 */
  targetScrollHour: number | null;

  // Teacher Focus (Single-Teacher Mode)
  /** Currently selected teacher ID for timeline display */
  selectedTeacherId: string | null;

  // Edit Mode (Precision Editing)
  /** Whether the timeline is in edit mode (resize handles visible) */
  isEditMode: boolean;

  // Zoom Animation State
  /** Whether the timeline is currently zooming (hide content for clean animation) */
  isZooming: boolean;

  // Long Press Animation State
  /** Whether long press "go to today" animation is in progress */
  isLongPressAnimating: boolean;

  // DateReel Animation State
  /** Whether DateReel is animating (click, pan, or nav button) */
  isDateReelAnimating: boolean;

  // SlideOver Panel State (Step 50)
  /** Whether the SlideOver panel is open */
  isSlideOverOpen: boolean;

  // Module Pad / Dock Customization
  /** Pinned modules in display order (persisted) */
  pinnedModules: AppModule[];
  /** Whether the module pad overlay is open (NOT persisted) */
  isModulePadOpen: boolean;

  // Drag Resize
  /** SidebarFinder 너비 (px) - 드래그 리사이즈 */
  finderWidth: number;
  /** CommandTerminal 높이 (px) - 드래그 리사이즈 */
  terminalHeight: number;

  // ISSUE_087: 전역 키보드 → Daily Log 진입
  /** 전역 키 캡처로 버퍼된 첫 글자 (null = 없음) */
  pendingTerminalChar: string | null;
}

/** Global UI actions */
export interface GlobalActions {
  // Layout
  /** Toggle the sidebar open/closed */
  toggleSidebar: () => void;
  /** Set sidebar open state directly */
  setSidebarOpen: (isOpen: boolean) => void;
  /** Toggle Dock collapsed state */
  toggleDock: () => void;
  /** Set Dock collapsed state directly */
  setDockCollapsed: (collapsed: boolean) => void;
  /** Toggle Finder collapsed state */
  toggleFinder: () => void;
  /** Set Finder collapsed state directly */
  setFinderCollapsed: (collapsed: boolean) => void;

  // Navigation
  /** Set the current active module */
  setCurrentModule: (module: AppModule) => void;

  // Theme
  /** Set the theme preference */
  setTheme: (theme: ThemePreference) => void;
  /** Toggle dark mode on/off */
  toggleTheme: () => void;
  /** Set dark mode directly */
  setDarkMode: (isDarkMode: boolean) => void;
  /** Set design style */
  setDesignStyle: (style: DesignStyle) => void;
  /** Set active custom theme ID directly (for settings sync) */
  setActiveCustomThemeId: (themeId: string | null) => void;

  // Custom Theme
  /** Activate a custom theme (sets designStyle to custom-{id} and caches palette) */
  activateCustomTheme: (themeId: string, palette: CustomThemePalette) => void;
  /** Deactivate custom theme (return to built-in theme) */
  deactivateCustomTheme: () => void;
  /** Update cached palette for a custom theme */
  updateCustomThemePalette: (themeId: string, palette: CustomThemePalette) => void;
  /** Remove a custom theme from cache */
  removeCustomThemePalette: (themeId: string) => void;

  // Timeline Mechanics (Zoom)
  /** Set hour width directly (clamped to 60-600px range) */
  setHourWidth: (width: number) => void;
  /** Zoom in: increase hourWidth */
  zoomIn: () => void;
  /** Zoom out: decrease hourWidth */
  zoomOut: () => void;
  /** Reset zoom to default (100px/hour) */
  resetZoom: () => void;
  /** Set base start time (0-23) */
  setBaseStartTime: (hour: number) => void;

  // Date Context
  /** Set the active date with source tracking. Optional targetScrollHour for precise scroll position (null = noon) */
  setActiveDate: (date: Date, source: ActiveDateSource, targetScrollHour?: number | null) => void;
  /** Navigate to today */
  goToToday: () => void;
  /** Navigate to previous day */
  goToPrevDay: () => void;
  /** Navigate to next day */
  goToNextDay: () => void;

  // Teacher Focus
  /** Set the selected teacher for timeline display */
  setSelectedTeacherId: (teacherId: string | null) => void;

  // Edit Mode
  /** Toggle edit mode on/off */
  toggleEditMode: () => void;
  /** Set edit mode directly */
  setEditMode: (isEditMode: boolean) => void;

  // Zoom Animation
  /** Set zooming state (for hiding content during zoom) */
  setIsZooming: (isZooming: boolean) => void;

  // Long Press Animation
  /** Set long press animation state */
  setIsLongPressAnimating: (isAnimating: boolean) => void;

  // DateReel Animation
  /** Set DateReel animation state */
  setIsDateReelAnimating: (isAnimating: boolean) => void;

  // SlideOver Panel (Step 50)
  /** Open the SlideOver panel */
  openSlideOver: () => void;
  /** Close the SlideOver panel */
  closeSlideOver: () => void;

  // Module Pad / Dock Customization
  /** Open the module pad overlay */
  openModulePad: () => void;
  /** Close the module pad overlay */
  closeModulePad: () => void;
  /** Toggle the module pad overlay */
  toggleModulePad: () => void;
  /** Pin a module to the dock (appended at end) */
  pinModule: (module: AppModule) => void;
  /** Unpin a module from the dock (REQUIRED modules cannot be unpinned) */
  unpinModule: (module: AppModule) => void;
  /** Reorder pinned modules (after drag-and-drop) */
  reorderPinnedModules: (newOrder: AppModule[]) => void;

  // Drag Resize
  /** Set SidebarFinder width (clamped 180-480) */
  setFinderWidth: (width: number) => void;
  /** Set CommandTerminal height (clamped 48-60vh) */
  setTerminalHeight: (height: number) => void;

  // ISSUE_087: 전역 키보드 → Daily Log 진입
  /** 전역 키 캡처 글자 버퍼 설정 */
  setPendingTerminalChar: (char: string | null) => void;
}

/** Combined store type */
export type GlobalStore = GlobalState & GlobalActions;

// ============================================================================
// HELPERS
// ============================================================================

/**
 * Clamp hour width to valid range
 */
function clampHourWidth(width: number): number {
  return Math.min(HOUR_WIDTH_MAX, Math.max(HOUR_WIDTH_MIN, width));
}

/**
 * Round hour width to 1 decimal place for smoother zoom
 * (prevents sub-pixel issues while allowing finer control)
 */
function roundHourWidth(width: number): number {
  return Math.round(width * 10) / 10;
}

/**
 * Add days to a date
 */
function addDays(date: Date, days: number): Date {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}

/**
 * Get today's date at midnight (local time)
 */
function getToday(): Date {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return today;
}

// ============================================================================
// DEFAULT STATE
// ============================================================================

const DEFAULT_STATE: GlobalState = {
  isSidebarOpen: true,
  isDockCollapsed: false,
  isFinderCollapsed: false,
  currentModule: 'DISPATCHER',
  theme: 'system',
  isDarkMode: false,
  designStyle: 'soft',
  activeCustomThemeId: null,
  customThemePalettes: {},
  hourWidth: HOUR_WIDTH_DEFAULT,
  baseStartTime: TIMELINE_START_HOUR,
  activeDate: getToday(),
  activeDateSource: 'init',
  activeDateKey: getLocalDayKey(getToday()),
  targetScrollHour: null, // null = use default noon (12:00)
  selectedTeacherId: null, // No teacher selected initially
  isEditMode: false, // Edit mode off by default
  isZooming: false, // Not zooming by default
  isLongPressAnimating: false, // Not long press animating by default
  isDateReelAnimating: false, // Not DateReel animating by default
  isSlideOverOpen: false, // SlideOver closed by default
  pinnedModules: DEFAULT_PINNED_MODULES,
  isModulePadOpen: false,
  finderWidth: 240,
  pendingTerminalChar: null,
  terminalHeight: 48,
};

// ============================================================================
// STORE IMPLEMENTATION
// ============================================================================

/**
 * useGlobalStore - Global UI State Management (V3)
 *
 * Manages OS-level UI state including:
 * - Sidebar visibility toggle
 * - Active module tracking
 * - Theme preferences
 * - Timeline hourWidth (60px to 600px per hour) - Professional Zoom Engine
 * - Active date for the timeline view
 *
 * Persistence:
 * - Persisted: currentModule, theme, hourWidth
 * - NOT persisted: isSidebarOpen, activeDate (reset to today on reload)
 *
 * @example
 * ```tsx
 * function TimelineGrid() {
 *   const { hourWidth, zoomIn, zoomOut, resetZoom } = useGlobalStore();
 *   const totalWidth = hourWidth * 24; // 24 hours
 *
 *   return (
 *     <div style={{ width: totalWidth }}>
 *       {/* Grid content *\/}
 *     </div>
 *   );
 * }
 * ```
 */
/**
 * pinnedModules 배열에 새 모듈을 앵커 기준으로 삽입한다.
 * - 이미 있으면 그대로(중복 방지).
 * - 앵커가 없으면 끝에 push(폴백).
 * ISSUE_194: migrate 의 splice-or-push 5복붙(v5·v6·v8·v9·v10)을 통합.
 * 동작은 특성 테스트(useGlobalStore.migrate.test.ts)로 고정돼 있다.
 */
export function insertModuleRelative(
  modules: AppModule[],
  anchor: AppModule,
  newModule: AppModule,
  position: "before" | "after",
): AppModule[] {
  if (modules.includes(newModule)) return modules;
  const next = [...modules];
  const idx = next.indexOf(anchor);
  if (idx === -1) {
    next.push(newModule);
  } else {
    next.splice(position === "after" ? idx + 1 : idx, 0, newModule);
  }
  return next;
}

/**
 * persist 버전 마이그레이션 (v0→v13). pinnedModules 순서를 앱 버전 간 보존한다.
 * ISSUE_194: 테스트 가능하도록 인라인에서 명명 함수로 추출(동작 보존).
 */
export function migrateGlobalState(persistedState: unknown, version: number) {
    const state = persistedState as Partial<GlobalState>;
    // If no version or old version, return fresh state
    if (version === 0) {
      return {};
    }
    // v1 -> v2: Add custom theme fields
    if (version === 1) {
      return {
        ...state,
        activeCustomThemeId: null,
        customThemePalettes: {},
        pinnedModules: DEFAULT_PINNED_MODULES,
      };
    }
    // v2 -> v3: Add pinnedModules
    if (version === 2) {
      return {
        ...state,
        pinnedModules: DEFAULT_PINNED_MODULES,
      };
    }
    // v3 -> v4: STATS(시험) → EXAMS 이관, STATS = 주간 대시보드
    if (version === 3) {
      // currentModule: 기존 STATS → EXAMS
      const currentModule = state.currentModule === 'STATS' ? 'EXAMS' as AppModule : state.currentModule;
      // pinnedModules: STATS → EXAMS, 그 뒤에 STATS(대시보드) 삽입
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        const idx = pinnedModules.indexOf('STATS' as AppModule);
        if (idx !== -1) {
          const next = [...pinnedModules];
          next[idx] = 'EXAMS' as AppModule;
          // STATS(대시보드)를 EXAMS 바로 뒤에 삽입
          next.splice(idx + 1, 0, 'STATS' as AppModule);
          pinnedModules = next;
        } else {
          // STATS가 없었다면 기본값에서 가져옴
          pinnedModules = DEFAULT_PINNED_MODULES;
        }
      }
      return { ...state, currentModule, pinnedModules };
    }
    // v4 -> v5: STUDENTS→SESSIONS rename, STUDENT_MANAGE 신설
    if (version === 4) {
      const currentModule = (state.currentModule as string) === 'STUDENTS' ? 'SESSIONS' as AppModule : state.currentModule;
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        const next = pinnedModules.map(m => (m as string) === 'STUDENTS' ? 'SESSIONS' as AppModule : m);
        const sessionsIdx = next.indexOf('SESSIONS' as AppModule);
        if (sessionsIdx !== -1 && !next.includes('STUDENT_MANAGE' as AppModule)) {
          next.splice(sessionsIdx + 1, 0, 'STUDENT_MANAGE' as AppModule);
        }
        pinnedModules = next;
      }
      return { ...state, currentModule, pinnedModules };
    }
    // v5 -> v6: TEXTBOOKS 모듈 추가
    if (version === 5) {
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        // EXAMS 뒤에 TEXTBOOKS 삽입
        pinnedModules = insertModuleRelative(pinnedModules, 'EXAMS' as AppModule, 'TEXTBOOKS' as AppModule, 'after');
      }
      return { ...state, pinnedModules };
    }
    // v6 -> v7: NOTEBOOKS 모듈 추가
    if (version === 6) {
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        // TEXTBOOKS 뒤에 NOTEBOOKS 삽입
        pinnedModules = insertModuleRelative(pinnedModules, 'TEXTBOOKS' as AppModule, 'NOTEBOOKS' as AppModule, 'after');
      }
      return { ...state, pinnedModules };
    }
    // v7 -> v8: EXAMS → TRACKS 모듈 교체 (ISSUE_105)
    if (version === 7) {
      const currentModule = state.currentModule === ('EXAMS' as AppModule) ? 'TRACKS' as AppModule : state.currentModule;
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        pinnedModules = pinnedModules.map(m => m === ('EXAMS' as AppModule) ? 'TRACKS' as AppModule : m);
      }
      return { ...state, currentModule, pinnedModules };
    }
    // v8 -> v9: SCORES 모듈 추가 (ISSUE_106)
    if (version === 8) {
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        // TRACKS 앞에 SCORES 삽입
        pinnedModules = insertModuleRelative(pinnedModules, 'TRACKS' as AppModule, 'SCORES' as AppModule, 'before');
      }
      return { ...state, pinnedModules };
    }
    // v9 -> v10: STUDENTS 모듈 추가 (ISSUE_106)
    if (version === 9) {
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        // STUDENT_MANAGE 뒤에 STUDENTS 삽입
        pinnedModules = insertModuleRelative(pinnedModules, 'STUDENT_MANAGE' as AppModule, 'STUDENTS' as AppModule, 'after');
      }
      return { ...state, pinnedModules };
    }
    // v10 -> v11: SCHOOLS 모듈 추가 (ISSUE_106)
    if (version === 10) {
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        // STUDENT_MANAGE 뒤에 SCHOOLS 삽입
        pinnedModules = insertModuleRelative(pinnedModules, 'STUDENT_MANAGE' as AppModule, 'SCHOOLS' as AppModule, 'after');
      }
      return { ...state, pinnedModules };
    }
    // v11 -> v12: STUDENTS + TRACKS 모듈 삭제 (ISSUE_106)
    if (version === 11) {
      const removed = new Set(['STUDENTS', 'TRACKS']);
      const currentModule = removed.has(state.currentModule as string) ? 'DISPATCHER' as AppModule : state.currentModule;
      let pinnedModules = state.pinnedModules;
      if (Array.isArray(pinnedModules)) {
        pinnedModules = pinnedModules.filter(m => !removed.has(m as string));
      }
      return { ...state, currentModule, pinnedModules };
    }
    return state;
}

export const useGlobalStore = create<GlobalStore>()(
  persist(
    (set) => ({
      // State
      ...DEFAULT_STATE,

      // Layout Actions
      toggleSidebar: () =>
        set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),

      setSidebarOpen: (isOpen) =>
        set({ isSidebarOpen: isOpen }),

      toggleDock: () =>
        set((state) => ({ isDockCollapsed: !state.isDockCollapsed })),

      setDockCollapsed: (collapsed) =>
        set({ isDockCollapsed: collapsed }),

      toggleFinder: () =>
        set((state) => ({ isFinderCollapsed: !state.isFinderCollapsed })),

      setFinderCollapsed: (collapsed) =>
        set({ isFinderCollapsed: collapsed }),

      // Navigation Actions
      setCurrentModule: (module) => {
        set({ currentModule: module });
        if (typeof window !== 'undefined') {
          queueMicrotask(() => {
            // OAuth 콜백 해시(#access_token=…)를 덮어쓰지 않는다.
            // SDK가 _initialize() 잠금 안에서 비동기로 파싱하므로, 같은 틱에
            // replaceState로 해시를 갈아치우면 auth 파라미터를 놓친다. (ISSUE_168 M-2)
            const hash = window.location.hash;
            if (hash.includes('access_token') || hash.includes('refresh_token')) {
              return;
            }
            window.history.replaceState(null, '', `/#${module.toLowerCase()}`);
          });
        }
      },

      // Theme Actions
      setTheme: (theme) =>
        set({ theme }),

      toggleTheme: () =>
        set((state) => ({ isDarkMode: !state.isDarkMode })),

      setDarkMode: (isDarkMode) =>
        set({ isDarkMode }),

      setDesignStyle: (style) =>
        set({ designStyle: style }),

      setActiveCustomThemeId: (themeId) =>
        set({
          activeCustomThemeId: themeId,
          // If setting a custom theme, update designStyle
          // If clearing, keep current designStyle (user might want soft/brutal)
          ...(themeId ? { designStyle: `custom-${themeId}` as DesignStyle } : {}),
        }),

      // Custom Theme Actions
      activateCustomTheme: (themeId, palette) =>
        set((state) => ({
          designStyle: `custom-${themeId}` as DesignStyle,
          activeCustomThemeId: themeId,
          customThemePalettes: {
            ...state.customThemePalettes,
            [themeId]: palette,
          },
        })),

      deactivateCustomTheme: () =>
        set({
          designStyle: 'soft',
          activeCustomThemeId: null,
        }),

      updateCustomThemePalette: (themeId, palette) =>
        set((state) => ({
          customThemePalettes: {
            ...state.customThemePalettes,
            [themeId]: palette,
          },
        })),

      removeCustomThemePalette: (themeId) =>
        set((state) => {
          const { [themeId]: removed, ...rest } = state.customThemePalettes;
          return {
            customThemePalettes: rest,
            // If removing the active theme, reset to soft
            ...(state.activeCustomThemeId === themeId
              ? { designStyle: 'soft' as DesignStyle, activeCustomThemeId: null }
              : {}),
          };
        }),

      // Timeline Zoom Actions (hourWidth-based)
      setHourWidth: (width) =>
        set({ hourWidth: roundHourWidth(clampHourWidth(width)) }),

      zoomIn: () =>
        set((state) => ({
          hourWidth: roundHourWidth(clampHourWidth(state.hourWidth + HOUR_WIDTH_STEP)),
        })),

      zoomOut: () =>
        set((state) => ({
          hourWidth: roundHourWidth(clampHourWidth(state.hourWidth - HOUR_WIDTH_STEP)),
        })),

      resetZoom: () =>
        set({ hourWidth: HOUR_WIDTH_DEFAULT }),

      setBaseStartTime: (hour) =>
        set({ baseStartTime: Math.max(0, Math.min(23, Math.floor(hour))) }),

      // Date Context Actions
      setActiveDate: (date, source, targetScrollHour = null) => {
        const newKey = getLocalDayKey(date);
        set((state) => {
          // 같은 날짜면 source와 targetScrollHour만 업데이트 (불필요한 리렌더 방지)
          if (state.activeDateKey === newKey) {
            return { activeDateSource: source, targetScrollHour };
          }
          return {
            activeDate: date,
            activeDateSource: source,
            activeDateKey: newKey,
            targetScrollHour,
          };
        });
      },

      goToToday: () => {
        const today = getToday();
        set({
          activeDate: today,
          activeDateSource: 'go-to-today',
          activeDateKey: getLocalDayKey(today),
          targetScrollHour: null, // Reset to noon
        });
      },

      goToPrevDay: () =>
        set((state) => {
          const newDate = addDays(state.activeDate, -1);
          return {
            activeDate: newDate,
            activeDateSource: 'nav-button',
            activeDateKey: getLocalDayKey(newDate),
            targetScrollHour: null, // Reset to noon
          };
        }),

      goToNextDay: () =>
        set((state) => {
          const newDate = addDays(state.activeDate, 1);
          return {
            activeDate: newDate,
            activeDateSource: 'nav-button',
            activeDateKey: getLocalDayKey(newDate),
            targetScrollHour: null, // Reset to noon
          };
        }),

      // Teacher Focus Actions
      setSelectedTeacherId: (teacherId) =>
        set({ selectedTeacherId: teacherId }),

      // Edit Mode Actions
      toggleEditMode: () =>
        set((state) => ({ isEditMode: !state.isEditMode })),

      setEditMode: (isEditMode) =>
        set({ isEditMode }),

      // Zoom Animation Actions
      setIsZooming: (isZooming) =>
        set({ isZooming }),

      setIsLongPressAnimating: (isAnimating) =>
        set({ isLongPressAnimating: isAnimating }),

      setIsDateReelAnimating: (isAnimating) =>
        set({ isDateReelAnimating: isAnimating }),

      // SlideOver Panel Actions (Step 50)
      openSlideOver: () =>
        set({ isSlideOverOpen: true }),

      closeSlideOver: () =>
        set({ isSlideOverOpen: false }),

      // Module Pad / Dock Customization Actions
      openModulePad: () =>
        set({ isModulePadOpen: true }),

      closeModulePad: () =>
        set({ isModulePadOpen: false }),

      toggleModulePad: () =>
        set((state) => ({ isModulePadOpen: !state.isModulePadOpen })),

      pinModule: (module) =>
        set((state) => {
          if (state.pinnedModules.includes(module)) return state;
          return { pinnedModules: [...state.pinnedModules, module] };
        }),

      unpinModule: (module) =>
        set((state) => {
          // Required modules cannot be unpinned
          if (REQUIRED_MODULES.includes(module)) return state;
          const next = state.pinnedModules.filter((m) => m !== module);
          // If current module is being unpinned, switch to DISPATCHER
          const currentModule = state.currentModule === module ? 'DISPATCHER' as AppModule : state.currentModule;
          return { pinnedModules: next, currentModule };
        }),

      reorderPinnedModules: (newOrder) =>
        set({ pinnedModules: newOrder }),

      // Drag Resize Actions
      setFinderWidth: (width) =>
        set({ finderWidth: Math.round(Math.min(480, Math.max(180, width))) }),

      setTerminalHeight: (height) =>
        set({ terminalHeight: Math.round(Math.min(window.innerHeight * 0.6, Math.max(48, height))) }),

      setPendingTerminalChar: (char) => set({ pendingTerminalChar: char }),
    }),
    {
      name: 'noten-global',
      version: 13, // v13: QUESTIONS 모듈 추가 (ISSUE_112)
      storage: createJSONStorage(() => localStorage),
      // Persist: currentModule, theme, isDarkMode, designStyle, custom theme state, pinnedModules
      // Do NOT persist: hourWidth (always default), isSidebarOpen, activeDate, baseStartTime, isModulePadOpen
      partialize: (state) => ({
        currentModule: state.currentModule,
        theme: state.theme,
        isDarkMode: state.isDarkMode,
        designStyle: state.designStyle,
        activeCustomThemeId: state.activeCustomThemeId,
        customThemePalettes: state.customThemePalettes,
        pinnedModules: state.pinnedModules,
        finderWidth: state.finderWidth,
      }),
      // Merge persisted state with fresh defaults (ensures activeDate is today, hourWidth is default)
      merge: (persistedState, currentState) => {
        const today = getToday();
        const persisted = persistedState as Partial<GlobalState>;

        // Guard: 삭제·게이팅된 모듈(CLASSES, 출시 숨김 QUESTIONS 등)이 persist/딥링크에
        // 남아있으면 DISPATCHER로 복귀. VALID_MODULE_IDS 는 featureFlags 게이팅이 반영된
        // "현재 노출 모듈" 집합 → 출시 빌드에서 #questions 딥링크/스테일 currentModule 자동 폴백.
        const validModules: AppModule[] = VALID_MODULE_IDS;

        // URL 해시가 있으면 localStorage보다 우선 (딥링크/공유 지원)
        const hashModule = typeof window !== 'undefined'
          ? window.location.hash.replace('#', '').toUpperCase() as AppModule
          : undefined;
        const fromHash = hashModule && validModules.includes(hashModule) ? hashModule : undefined;

        const currentModule = fromHash
          ?? (validModules.includes(persisted.currentModule as AppModule)
            ? persisted.currentModule as AppModule
            : 'DISPATCHER');

        // Validate persisted pinnedModules — filter stale IDs, ensure DISPATCHER
        let pinnedModules = DEFAULT_PINNED_MODULES;
        if (Array.isArray(persisted.pinnedModules)) {
          const filtered = persisted.pinnedModules.filter(
            (id): id is AppModule => VALID_MODULE_IDS.includes(id as AppModule)
          );
          // Ensure all required modules are present
          for (const req of REQUIRED_MODULES) {
            if (!filtered.includes(req)) {
              filtered.unshift(req);
            }
          }
          if (filtered.length > 0) {
            pinnedModules = filtered;
          }
        }

        return {
          ...currentState,
          ...persisted,
          currentModule,
          pinnedModules,
          // Always use fresh values (not persisted)
          hourWidth: HOUR_WIDTH_DEFAULT, // Always reset to default
          activeDate: today,
          activeDateSource: 'init' as ActiveDateSource,
          activeDateKey: getLocalDayKey(today),
          targetScrollHour: null,
          isSidebarOpen: true,
          baseStartTime: TIMELINE_START_HOUR,
          isModulePadOpen: false, // Always closed on load
          // Drag Resize: persist된 값 복원 (undefined → 기본값)
          finderWidth: persisted.finderWidth ?? 240,
          terminalHeight: 48, // 항상 접힌 상태로 시작
        };
      },
      // Handle version migrations
      migrate: migrateGlobalState,
    }
  )
);

// ============================================================================
// URL HASH SYNC — 초기 로드 시 해시 설정
// ============================================================================
if (typeof window !== 'undefined') {
  // OAuth implicit flow 토큰이 해시에 있으면 덮어쓰지 않음
  // (Supabase SDK가 #access_token= 을 처리해야 함)
  const _hash = window.location.hash;
  const _hasAuthTokens = _hash.includes('access_token=') || _hash.includes('error_description=') || window.location.search.includes('code=');
  // 모듈 해시는 메인앱(/)에서만. /spaces·/settings·/login 등 standalone 라우트엔 안 붙임. (ISSUE_182)
  if (!_hasAuthTokens && window.location.pathname === '/') {
    const initial = useGlobalStore.getState().currentModule;
    window.history.replaceState(
      null,
      '',
      `${window.location.pathname}${window.location.search}#${initial.toLowerCase()}`,
    );
  }
}

// ============================================================================
// SELECTOR HOOKS (Convenience hooks for common patterns)
// ============================================================================

/**
 * Get current theme preference
 */
export const useTheme = () => useGlobalStore((state) => state.theme);

/**
 * Get dark mode state
 */
export const useIsDarkMode = () => useGlobalStore((state) => state.isDarkMode);

/**
 * Get current active module
 */
export const useCurrentModule = () => useGlobalStore((state) => state.currentModule);

/**
 * Get sidebar state
 */
export const useSidebarOpen = () => useGlobalStore((state) => state.isSidebarOpen);

/**
 * Get Dock collapsed state
 */
export const useIsDockCollapsed = () => useGlobalStore((state) => state.isDockCollapsed);

/**
 * Get Dock toggle action
 */
export const useToggleDock = () => useGlobalStore((state) => state.toggleDock);

/**
 * Get Finder collapsed state
 */
export const useIsFinderCollapsed = () => useGlobalStore((state) => state.isFinderCollapsed);

/**
 * Get Finder toggle action
 */
export const useToggleFinder = () => useGlobalStore((state) => state.toggleFinder);

/**
 * Get current hour width (zoom level)
 */
export const useHourWidth = () => useGlobalStore((state) => state.hourWidth);

/**
 * Get active date
 */
export const useActiveDate = () => useGlobalStore((state) => state.activeDate);

/**
 * Get active date source (for sync control)
 */
export const useActiveDateSource = () => useGlobalStore((state) => state.activeDateSource);

/**
 * Get active date key (local dayKey)
 */
export const useActiveDateKey = () => useGlobalStore((state) => state.activeDateKey);

/**
 * Get target scroll hour (null = use noon)
 */
export const useTargetScrollHour = () => useGlobalStore((state) => state.targetScrollHour);

// ⚠️ PERF #004: 객체 반환 셀렉터 제거됨 (useTimelineZoom, useDateNavigation, useTimelineConfig)
// 매 렌더마다 새 {} 참조 생성 → 모든 구독 컴포넌트 cascade 리렌더.
// 대신 위의 원자적 셀렉터 (useHourWidth, useActiveDate 등) 사용할 것.
// 파생값 필요 시 컴포넌트에서 직접 계산: const canZoomIn = hourWidth < HOUR_WIDTH_MAX;

/**
 * Get selected teacher ID (read-only)
 *
 * IMPORTANT: Do NOT return objects from Zustand selectors!
 * Returning `{ a, b }` creates a NEW object every render → infinite loop.
 * Use separate selectors for each value instead.
 */
export const useSelectedTeacherId = () =>
  useGlobalStore((state) => state.selectedTeacherId);

/**
 * Get selected teacher setter (action only)
 */
export const useSetSelectedTeacherId = () =>
  useGlobalStore((state) => state.setSelectedTeacherId);

/**
 * Get edit mode state
 */
export const useIsEditMode = () =>
  useGlobalStore((state) => state.isEditMode);

/**
 * Get edit mode toggle action
 */
export const useToggleEditMode = () =>
  useGlobalStore((state) => state.toggleEditMode);

/**
 * Get zooming state (for hiding content during zoom)
 */
export const useIsZooming = () =>
  useGlobalStore((state) => state.isZooming);

/**
 * Get set zooming action
 */
export const useSetIsZooming = () =>
  useGlobalStore((state) => state.setIsZooming);

/**
 * Get long press animation state
 */
export const useIsLongPressAnimating = () =>
  useGlobalStore((state) => state.isLongPressAnimating);
export const useSetIsLongPressAnimating = () =>
  useGlobalStore((state) => state.setIsLongPressAnimating);

/**
 * Get DateReel animation state
 */
export const useIsDateReelAnimating = () =>
  useGlobalStore((state) => state.isDateReelAnimating);
export const useSetIsDateReelAnimating = () =>
  useGlobalStore((state) => state.setIsDateReelAnimating);

/**
 * Get SlideOver open state
 */
export const useIsSlideOverOpen = () =>
  useGlobalStore((state) => state.isSlideOverOpen);

/**
 * Get SlideOver actions
 */
export const useOpenSlideOver = () =>
  useGlobalStore((state) => state.openSlideOver);

export const useCloseSlideOver = () =>
  useGlobalStore((state) => state.closeSlideOver);

// ============================================================================
// CUSTOM THEME SELECTORS
// ============================================================================

/**
 * Get active custom theme ID
 */
export const useActiveCustomThemeId = () =>
  useGlobalStore((state) => state.activeCustomThemeId);

/**
 * Get custom theme palettes cache
 */
export const useCustomThemePalettes = () =>
  useGlobalStore((state) => state.customThemePalettes);

/**
 * Get activate custom theme action
 */
export const useActivateCustomTheme = () =>
  useGlobalStore((state) => state.activateCustomTheme);

/**
 * Get deactivate custom theme action
 */
export const useDeactivateCustomTheme = () =>
  useGlobalStore((state) => state.deactivateCustomTheme);

/**
 * Get design style (read-only)
 */
export const useDesignStyle = () =>
  useGlobalStore((state) => state.designStyle);


/**
 * Check if current theme is a custom theme
 */
export const useIsCustomTheme = () =>
  useGlobalStore((state) => state.designStyle.startsWith('custom-'));

// ============================================================================
// MODULE PAD / DOCK CUSTOMIZATION SELECTORS
// ============================================================================

export const usePinnedModules = () =>
  useGlobalStore((state) => state.pinnedModules);

export const useIsModulePadOpen = () =>
  useGlobalStore((state) => state.isModulePadOpen);

export const useOpenModulePad = () =>
  useGlobalStore((state) => state.openModulePad);

export const useCloseModulePad = () =>
  useGlobalStore((state) => state.closeModulePad);

export const useToggleModulePad = () =>
  useGlobalStore((state) => state.toggleModulePad);

export const usePinModule = () =>
  useGlobalStore((state) => state.pinModule);

export const useUnpinModule = () =>
  useGlobalStore((state) => state.unpinModule);

export const useReorderPinnedModules = () =>
  useGlobalStore((state) => state.reorderPinnedModules);

/**
 * Get current section (derived from currentModule)
 */
export function useCurrentSection(): ModuleSection {
  const currentModule = useGlobalStore((s) => s.currentModule);
  return getModuleDefinition(currentModule).section;
}

// ============================================================================
// DRAG RESIZE SELECTORS
// ============================================================================

export const useFinderWidth = () =>
  useGlobalStore((state) => state.finderWidth);

export const useSetFinderWidth = () =>
  useGlobalStore((state) => state.setFinderWidth);

export const useTerminalHeight = () =>
  useGlobalStore((state) => state.terminalHeight);

export const useSetTerminalHeight = () =>
  useGlobalStore((state) => state.setTerminalHeight);

export const usePendingTerminalChar = () =>
  useGlobalStore((state) => state.pendingTerminalChar);

export const useSetPendingTerminalChar = () =>
  useGlobalStore((state) => state.setPendingTerminalChar);

export default useGlobalStore;
